import {
  PreviewTask,
  TaskDefaultStatusId,
  TaskPriority,
} from "@jugl-web/rest-api/tasks";
import { TaskCustomFieldModel } from "@jugl-web/rest-api/tasks/models/TaskCustomField";
import { isoToLocalDate } from "@jugl-web/utils";
import isPast from "date-fns/isPast";
import isWithinInterval from "date-fns/isWithinInterval";
import { useCallback } from "react";
import { useBoardPermissionsFactory } from "./useBoardPermissions";
import { useTaskLabels } from "./useTaskLabels";
import { useTaskStatuses } from "./useTaskStatuses";
import { useTaskStatusesGetters } from "./useTaskStatusesGetters";

interface UseTaskSelectorsOptions {
  entityId: string;
  meId: string;
  boardId: string | null;
}

export const useTaskSelectors = ({
  entityId,
  meId,
  boardId,
}: UseTaskSelectorsOptions) => {
  const { getLabelById } = useTaskLabels({ entityId });
  const { getStatusByIdWithFallback } = useTaskStatuses({ entityId, boardId });
  const { getStatusByBoard } = useTaskStatusesGetters({ entityId });

  const getBoardPermissions = useBoardPermissionsFactory({ entityId, meId });

  const createSelectTasksByDueDateInterval = useCallback(
    (tasks: PreviewTask[]) => (from: Date, to: Date) =>
      tasks.filter((task) => {
        if (task.due_at === null) {
          return false;
        }

        return isWithinInterval(isoToLocalDate(task.due_at), {
          start: from,
          end: to,
        });
      }),
    []
  );

  const createSelectTasksByCalendarDay = useCallback(
    (tasks: PreviewTask[]) => (day: number) =>
      tasks.filter((task) => {
        if (task.due_at === null) {
          return false;
        }
        return isoToLocalDate(task.due_at).getDate() === day;
      }),
    []
  );

  const createSelectOverdueTasks = useCallback(
    (tasks: PreviewTask[]) =>
      (options: { excludeInterval?: Interval } = {}) =>
        tasks.filter((task) => {
          if (task.status === TaskDefaultStatusId.completed) {
            return false;
          }

          if (task.due_at === null) {
            return false;
          }

          const dueAtDate = isoToLocalDate(task.due_at);

          const isOutOfRange =
            options.excludeInterval &&
            isWithinInterval(dueAtDate, options.excludeInterval);

          if (isOutOfRange) {
            return false;
          }

          return isPast(dueAtDate);
        }),
    []
  );

  const createSelectNoDueDateTasks = useCallback(
    (tasks: PreviewTask[]) => () =>
      tasks.filter((task) => task.due_at === null),
    []
  );

  const createSelectTasksByLabelId = useCallback(
    (tasks: PreviewTask[]) => (labelId: string | null) =>
      tasks.filter((task) => {
        if (labelId) {
          return task.label_id === labelId;
        }

        // To get all tasks without label, we return tasks without label or tasks
        // with label that doesn't exist anymore
        return task.label_id === null || !getLabelById(task.label_id);
      }),
    [getLabelById]
  );

  const createSelectTasksByStatusId = useCallback(
    (tasks: PreviewTask[]) => (statusId: string) =>
      tasks.filter((task) => {
        const { id: actualStatusIdOrFallbackStatusId } =
          getStatusByIdWithFallback(task.status);

        return statusId === actualStatusIdOrFallbackStatusId;
      }),
    [getStatusByIdWithFallback]
  );

  const createSelectTasksByStatusName = useCallback(
    (tasks: PreviewTask[]) => (statusName: string) =>
      tasks.filter((task) => {
        const taskStatus = getStatusByBoard(task.board_id, task.status);

        if (!taskStatus) {
          return false;
        }

        return statusName === taskStatus.text;
      }),
    [getStatusByBoard]
  );

  const createSelectTasksByReporteeId = useCallback(
    (tasks: PreviewTask[]) => (userId: string) =>
      tasks.filter(
        (task) =>
          task.assignees.includes(userId) ||
          (task.created_by === userId && task.assignees.length === 0)
      ),
    []
  );

  const createSelectTasksByAssigneeId = useCallback(
    (tasks: PreviewTask[]) => (userId: string | null) => {
      if (userId === null) {
        return tasks.filter((task) => task.assignees.length === 0);
      }

      return tasks.filter((task) => task.assignees.includes(userId));
    },
    []
  );

  const createSelectTasksByBoardId = useCallback(
    (tasks: PreviewTask[]) => (taskBoardId: string | null) =>
      tasks.filter((task) => task.board_id === taskBoardId),
    []
  );

  const createSelectTasksFromRestrictedBoards = useCallback(
    (tasks: PreviewTask[]) => () =>
      tasks.filter((task) => {
        if (!task.board_id) {
          return false;
        }

        return !getBoardPermissions(task.board_id).canAccessBoard;
      }),
    [getBoardPermissions]
  );

  const createSelectTasksByCustomerId = useCallback(
    (tasks: PreviewTask[]) => (customerId: string | null) =>
      tasks.filter((task) => task.cust_id === customerId),
    []
  );

  const createSelectTasksByPriority = useCallback(
    (tasks: PreviewTask[]) => (taskPriority: TaskPriority) =>
      tasks.filter((task) => task.priority === taskPriority),
    []
  );

  const createSelectTasksByCustomDropdownFieldValue = useCallback(
    (tasks: PreviewTask[]) =>
      (id: string, value: string | null, customField: TaskCustomFieldModel) =>
        tasks.filter((task) => {
          if (!value) {
            return (
              !task.custom_fields?.[id] ||
              // Return also tasks which have selected options
              // that don't exist in custom field anymore
              (task.custom_fields?.[id] &&
                !customField.values?.find(
                  (dropdownOption) =>
                    dropdownOption.id === task.custom_fields?.[id]
                ))
            );
          }
          return task.custom_fields?.[id] === value;
        }),
    []
  );

  return {
    createSelectTasksByDueDateInterval,
    createSelectOverdueTasks,
    createSelectNoDueDateTasks,
    createSelectTasksByLabelId,
    createSelectTasksByStatusId,
    createSelectTasksByStatusName,
    createSelectTasksByReporteeId,
    createSelectTasksByAssigneeId,
    createSelectTasksByBoardId,
    createSelectTasksFromRestrictedBoards,
    createSelectTasksByCustomerId,
    createSelectTasksByPriority,
    createSelectTasksByCustomDropdownFieldValue,
    createSelectTasksByCalendarDay,
  };
};
